import { CANONICAL_PLANT_TRAITS, PLANTS, type Plant, type PlantTrait } from '@/data/plants';
import { getCommunityPlants } from '@/lib/communityPlants';
import { getGeneratedPlantById, searchGeneratedPlants } from '@/lib/generatedProfiles';
import {
  type CanonicalPlant,
  normalizeBuiltInPlant,
  normalizeCommunityPlant,
  normalizeGeneratedPlantToPlant,
} from '@/lib/normalizePlant';
import { plantIsNativeToSubdivision } from '@/lib/nativeRange';
import { enrichPlantNativeRangeForDisplay } from '@/lib/nativeRangeEnrichment';
import {
  normalizeUserGardenLocation,
  type UserGardenLocation,
} from '@/lib/userGardenLocation';

export type PlantCatalogSourceLabel = 'Community Plant';
export type PlantCatalogCategory = 'all' | Plant['type'];
export type PlantCatalogTypeFilter = PlantCatalogCategory | 'my-zone' | 'native-to-me' | 'tree' | 'cover-crop' | 'grass' | 'grain';
export type PlantCatalogTraitFilter = 'tree' | 'cover-crop' | 'grass' | 'grain';
export type PlantCatalogSortMode = 'canonical';

export interface PlantCatalogFilterContext {
  category: PlantCatalogCategory;
  searchQuery: string;
  traitFilters: PlantTrait[];
  nativeFilter: boolean;
  zoneFilter: boolean;
  treeFilter: boolean;
  coverCropFilter: boolean;
  grassFilter: boolean;
  grainFilter: boolean;
  userZone: number | null;
  gardenLocation: UserGardenLocation | null;
  sortMode: PlantCatalogSortMode;
}

export const DEFAULT_PLANT_CATALOG_FILTER_CONTEXT: PlantCatalogFilterContext = {
  category: 'all',
  searchQuery: '',
  traitFilters: [],
  nativeFilter: false,
  zoneFilter: false,
  treeFilter: false,
  coverCropFilter: false,
  grassFilter: false,
  grainFilter: false,
  userZone: null,
  gardenLocation: null,
  sortMode: 'canonical',
};

interface PlantCatalogOptions {
  includeDraftGenerated?: boolean;
  generatedQuery?: string;
}

interface PlantSearchOptions extends PlantCatalogOptions {
  limit?: number;
}

type RawNavigationSearchParams =
  | URLSearchParams
  | Record<string, string | string[] | undefined>
  | null
  | undefined;

const CANONICAL_PLANT_TRAIT_SET = new Set<PlantTrait>(CANONICAL_PLANT_TRAITS);
const PLANT_CATALOG_CATEGORIES = new Set<PlantCatalogCategory>([
  'all',
  'vegetable',
  'herb',
  'fruit',
  'flower',
]);
const PLANT_SEARCH_ALIASES: Record<string, string[]> = {
  zucchini: ['courgette'],
  'bell-pepper': ['capsicum', 'sweet pepper'],
  coriander: ['cilantro'],
  arugula: ['rocket'],
  eggplant: ['aubergine'],
  scallions: ['green onion', 'spring onion'],
  serviceberry: ['juneberry', 'shadbush', 'saskatoon'],
};

export interface PlantCatalogSearchRelevance {
  score: number;
  directMatch: boolean;
}

function firstSearchParamValue(searchParams: RawNavigationSearchParams, key: string): string | undefined {
  if (!searchParams) return undefined;
  if (searchParams instanceof URLSearchParams) return searchParams.get(key) ?? undefined;

  const value = searchParams[key];
  if (Array.isArray(value)) return value[0];
  return value;
}

function normalizeCatalogCategory(value: string | undefined): PlantCatalogCategory {
  return value && PLANT_CATALOG_CATEGORIES.has(value as PlantCatalogCategory)
    ? value as PlantCatalogCategory
    : 'all';
}

function normalizeBooleanQueryValue(value: string | undefined): boolean {
  return value === '1' || value === 'true';
}

function normalizeUserZone(value: string | undefined): number | null {
  if (!value) return null;
  const parsed = Number(value);
  return Number.isInteger(parsed) && parsed >= 1 && parsed <= 13 ? parsed : null;
}

function normalizeTraitFilters(value: string | undefined): PlantTrait[] {
  if (!value) return [];
  const traits = value
    .split(',')
    .map(trait => trait.trim())
    .filter((trait): trait is PlantTrait => CANONICAL_PLANT_TRAIT_SET.has(trait as PlantTrait));

  return Array.from(new Set(traits));
}

function queryParamBoolean(value: boolean): string {
  return value ? '1' : '0';
}

function normalizeComparableText(value: string): string {
  return value
    .normalize('NFKD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase()
    .replace(/[’']/g, '')
    .replace(/[^a-z0-9]+/g, ' ')
    .trim();
}

function sourceRank(plant: CanonicalPlant): number {
  if (plant.source?.kind === 'curated') return 0;
  if (plant.status === 'draft') return 2;
  if (plant.source?.kind === 'community') return 1;
  return 1;
}

function traditionalMedicinalUseSearchText(plant: CanonicalPlant): string {
  const use = plant.traditionalMedicinalUse;
  if (!use) return '';

  return [
    use.overview,
    ...(use.partsUsed ?? []),
    ...(use.historicalContexts ?? []).flatMap(context => [
      context.tradition ?? '',
      context.plantPart ?? '',
      context.description,
    ]),
  ].join(' ');
}

function plantAliasSearchTexts(plant: CanonicalPlant): string[] {
  const aliases = new Set<string>([
    plant.id.replace(/-/g, ' '),
    ...(PLANT_SEARCH_ALIASES[plant.id] ?? []),
  ]);

  return [...aliases]
    .map(alias => normalizeComparableText(alias))
    .filter(Boolean);
}

function plantTagSearchTexts(plant: CanonicalPlant): string[] {
  return [
    plant.type,
    ...(plant.traits ?? []),
  ].map(value => normalizeComparableText(value));
}

function plantContentSearchText(plant: CanonicalPlant): string {
  return normalizeComparableText([
    plant.description,
    plant.naturalHistory ?? '',
    plant.morphology?.rootSystem ?? '',
    plant.morphology?.stem ?? '',
    plant.morphology?.leaves ?? '',
    plant.morphology?.flowers ?? '',
    plant.morphology?.fruit ?? '',
    ...(plant.varieties ?? []).flatMap(variety => [
      variety.name,
      variety.description,
      variety.bestFor ?? '',
    ]),
    traditionalMedicinalUseSearchText(plant),
  ].join(' '));
}

function plantReferenceSearchText(plant: CanonicalPlant): string {
  return normalizeComparableText([
    ...plant.companions,
    ...plant.antagonists,
    ...plant.pests,
  ].join(' '));
}

export function getPlantCatalogSearchRelevance(
  plant: CanonicalPlant,
  query: string,
): PlantCatalogSearchRelevance {
  const normalizedQuery = normalizeComparableText(query);
  if (!normalizedQuery) return { score: 0, directMatch: false };

  const normalizedName = normalizeComparableText(plant.name);
  const normalizedScientificName = normalizeComparableText(plant.scientificName);
  const aliases = plantAliasSearchTexts(plant);
  const tags = plantTagSearchTexts(plant);
  const contentText = plantContentSearchText(plant);
  const referenceText = plantReferenceSearchText(plant);

  if (normalizedName === normalizedQuery) {
    return { score: 1000, directMatch: true };
  }

  if (normalizedScientificName === normalizedQuery) {
    return { score: 950, directMatch: true };
  }

  if (aliases.includes(normalizedQuery)) {
    return { score: 900, directMatch: true };
  }

  if (normalizedName.startsWith(normalizedQuery)) {
    return { score: 850, directMatch: true };
  }

  if (normalizedName.includes(normalizedQuery)) {
    return { score: 800, directMatch: true };
  }

  if (normalizedScientificName.includes(normalizedQuery)) {
    return { score: 750, directMatch: false };
  }

  if (tags.some(tag => tag === normalizedQuery || tag.includes(normalizedQuery))) {
    return { score: 600, directMatch: false };
  }

  if (contentText.includes(normalizedQuery)) {
    return { score: 400, directMatch: false };
  }

  if (referenceText.includes(normalizedQuery)) {
    return { score: 250, directMatch: false };
  }

  return { score: 0, directMatch: false };
}

export function getPlantSourceLabel(plant: CanonicalPlant): PlantCatalogSourceLabel | null {
  if (plant.source?.kind === 'community' || plant.status === 'draft') return 'Community Plant';
  return null;
}

export function sortCanonicalPlants(a: CanonicalPlant, b: CanonicalPlant): number {
  const rankDelta = sourceRank(a) - sourceRank(b);
  if (rankDelta !== 0) return rankDelta;
  return a.name.localeCompare(b.name);
}

export function mergeCanonicalPlants(...groups: CanonicalPlant[][]): CanonicalPlant[] {
  const byId = new Map<string, CanonicalPlant>();

  for (const plant of groups.flat()) {
    const existing = byId.get(plant.id);
    if (!existing || sourceRank(plant) < sourceRank(existing)) {
      byId.set(plant.id, plant);
    }
  }

  return [...byId.values()].sort(sortCanonicalPlants);
}

export function buildCanonicalPlantLookup(plants: CanonicalPlant[]): Map<string, CanonicalPlant> {
  return new Map(plants.map(plant => [plant.id, plant]));
}

export function normalizeBuiltInCatalogPlant(plant: (typeof PLANTS)[number]): CanonicalPlant {
  return normalizeBuiltInPlant(enrichPlantNativeRangeForDisplay(plant));
}

export function normalizeCommunityCatalogPlant(plant: Parameters<typeof normalizeCommunityPlant>[0]): CanonicalPlant {
  return normalizeCommunityPlant(enrichPlantNativeRangeForDisplay(plant));
}

export function normalizeGeneratedCatalogPlant(
  plant: Parameters<typeof normalizeGeneratedPlantToPlant>[0],
): CanonicalPlant {
  return enrichPlantNativeRangeForDisplay(normalizeGeneratedPlantToPlant(plant));
}

export function listBuiltInCanonicalPlants(): CanonicalPlant[] {
  return PLANTS
    .map(normalizeBuiltInCatalogPlant)
    .sort(sortCanonicalPlants);
}

export function plantMatchesCatalogSearch(plant: CanonicalPlant, query: string): boolean {
  if (!query.trim()) return true;
  return getPlantCatalogSearchRelevance(plant, query).score > 0;
}

export function createPlantCatalogFilterContext({
  activeType = 'all',
  activeTraitTypeFilters = [],
  searchQuery = '',
  traitFilters = [],
  userZone = null,
  gardenLocation = null,
  sortMode = 'canonical',
}: {
  activeType?: PlantCatalogTypeFilter;
  activeTraitTypeFilters?: PlantCatalogTraitFilter[];
  searchQuery?: string;
  traitFilters?: PlantTrait[];
  userZone?: number | null;
  gardenLocation?: UserGardenLocation | null;
  sortMode?: PlantCatalogSortMode;
} = {}): PlantCatalogFilterContext {
  const nativeFilter = activeType === 'native-to-me';
  const zoneFilter = activeType === 'my-zone';
  const traitSet = new Set(activeTraitTypeFilters);
  // Legacy single-value support: if activeType is a trait filter and no explicit array given, honour it
  const treeFilter = traitSet.has('tree') || (activeTraitTypeFilters.length === 0 && activeType === 'tree');
  const coverCropFilter = traitSet.has('cover-crop') || (activeTraitTypeFilters.length === 0 && activeType === 'cover-crop');
  const grassFilter = traitSet.has('grass') || (activeTraitTypeFilters.length === 0 && activeType === 'grass');
  const grainFilter = traitSet.has('grain') || (activeTraitTypeFilters.length === 0 && activeType === 'grain');
  const traitOnlyActiveType = activeType === 'tree' || activeType === 'cover-crop' || activeType === 'grass' || activeType === 'grain';
  const category = activeType === 'native-to-me' || activeType === 'my-zone' || traitOnlyActiveType
    ? 'all'
    : activeType;

  return {
    category,
    searchQuery,
    traitFilters: Array.from(new Set(
      traitFilters.filter(trait => CANONICAL_PLANT_TRAIT_SET.has(trait)),
    )),
    nativeFilter,
    zoneFilter,
    treeFilter,
    coverCropFilter,
    grassFilter,
    grainFilter,
    userZone,
    gardenLocation,
    sortMode,
  };
}

export function filterAndSortCanonicalPlantCatalog(
  plants: CanonicalPlant[],
  context: PlantCatalogFilterContext = DEFAULT_PLANT_CATALOG_FILTER_CONTEXT,
): CanonicalPlant[] {
  const traitFilters = context.traitFilters ?? [];
  const trimmedQuery = context.searchQuery.trim();

  const filtered = plants
    .filter(plant => !trimmedQuery || plantMatchesCatalogSearch(plant, trimmedQuery))
    .filter(plant => {
      if (context.category !== 'all' && plant.type !== context.category) return false;
      if (context.treeFilter && !plant.traits?.includes('tree')) return false;
      if (context.coverCropFilter && !plant.traits?.includes('cover-crop')) return false;
      if (context.grassFilter && !plant.traits?.includes('grass')) return false;
      if (context.grainFilter && !plant.traits?.includes('grain')) return false;
      if (context.zoneFilter && !Boolean(context.userZone && plant.zones.includes(context.userZone))) return false;
      if (context.nativeFilter && !plantIsNativeToSubdivision(plant, context.gardenLocation)) return false;
      if (traitFilters.length > 0) {
        const plantTraits = new Set(plant.traits ?? []);
        return traitFilters.every(trait => plantTraits.has(trait));
      }

      return true;
    });

  if (!trimmedQuery) {
    return filtered.sort(sortCanonicalPlants);
  }

  return filtered.sort((left, right) => {
    const scoreDelta = getPlantCatalogSearchRelevance(right, trimmedQuery).score - getPlantCatalogSearchRelevance(left, trimmedQuery).score;
    if (scoreDelta !== 0) return scoreDelta;
    return left.name.localeCompare(right.name);
  });
}

export function serializePlantCatalogNavigationContext(
  context: PlantCatalogFilterContext = DEFAULT_PLANT_CATALOG_FILTER_CONTEXT,
): string {
  const params = new URLSearchParams();
  params.set('category', context.category);
  params.set('q', context.searchQuery.trim());
  params.set('native', queryParamBoolean(context.nativeFilter));
  params.set('zone', queryParamBoolean(context.zoneFilter));
  params.set('tree', queryParamBoolean(context.treeFilter));
  params.set('covercrop', queryParamBoolean(context.coverCropFilter));
  params.set('grass', queryParamBoolean(context.grassFilter));
  params.set('grain', queryParamBoolean(context.grainFilter));
  params.set('sort', context.sortMode);

  if (context.traitFilters.length > 0) params.set('traits', context.traitFilters.join(','));
  if (context.userZone) params.set('userZone', String(context.userZone));
  if (context.gardenLocation) {
    params.set('country', context.gardenLocation.countryCode);
    params.set('subdivision', context.gardenLocation.subdivisionCode);
  }

  return params.toString();
}

export function parsePlantCatalogNavigationContext(
  searchParams: RawNavigationSearchParams,
): PlantCatalogFilterContext | null {
  const hasContext = Boolean(
    firstSearchParamValue(searchParams, 'category') ||
    firstSearchParamValue(searchParams, 'q') ||
    firstSearchParamValue(searchParams, 'traits') ||
    firstSearchParamValue(searchParams, 'native') ||
    firstSearchParamValue(searchParams, 'zone') ||
    firstSearchParamValue(searchParams, 'tree') ||
    firstSearchParamValue(searchParams, 'covercrop') ||
    firstSearchParamValue(searchParams, 'grass') ||
    firstSearchParamValue(searchParams, 'grain') ||
    firstSearchParamValue(searchParams, 'sort') ||
    firstSearchParamValue(searchParams, 'userZone') ||
    firstSearchParamValue(searchParams, 'subdivision'),
  );
  if (!hasContext) return null;

  const countryCode = firstSearchParamValue(searchParams, 'country');
  const subdivisionCode = firstSearchParamValue(searchParams, 'subdivision');
  const gardenLocation = countryCode && subdivisionCode
    ? normalizeUserGardenLocation({
        countryCode,
        subdivisionCode,
        source: 'manual',
      })
    : null;

  return {
    category: normalizeCatalogCategory(firstSearchParamValue(searchParams, 'category')),
    searchQuery: firstSearchParamValue(searchParams, 'q')?.trim() ?? '',
    traitFilters: normalizeTraitFilters(firstSearchParamValue(searchParams, 'traits')),
    nativeFilter: normalizeBooleanQueryValue(firstSearchParamValue(searchParams, 'native')),
    zoneFilter: normalizeBooleanQueryValue(firstSearchParamValue(searchParams, 'zone')),
    treeFilter: normalizeBooleanQueryValue(firstSearchParamValue(searchParams, 'tree')),
    coverCropFilter: normalizeBooleanQueryValue(firstSearchParamValue(searchParams, 'covercrop')),
    grassFilter: normalizeBooleanQueryValue(firstSearchParamValue(searchParams, 'grass')),
    grainFilter: normalizeBooleanQueryValue(firstSearchParamValue(searchParams, 'grain')),
    userZone: normalizeUserZone(firstSearchParamValue(searchParams, 'userZone')),
    gardenLocation,
    sortMode: 'canonical',
  };
}

export function buildPlantDetailHref(
  plantId: string,
  context: PlantCatalogFilterContext = DEFAULT_PLANT_CATALOG_FILTER_CONTEXT,
): string {
  const query = serializePlantCatalogNavigationContext(context);
  return query ? `/plants/${plantId}?${query}` : `/plants/${plantId}`;
}

export function buildPlantLibraryHref(context: PlantCatalogFilterContext | null): string {
  if (!context) return '/plants';
  const query = serializePlantCatalogNavigationContext(context);
  return query ? `/plants?${query}` : '/plants';
}

function plantNameMatches(plant: CanonicalPlant, name: string): boolean {
  return plant.name.trim().toLowerCase() === name.trim().toLowerCase();
}

export async function listCanonicalPlantCatalog(options: PlantCatalogOptions = {}): Promise<CanonicalPlant[]> {
  const builtInPlants = listBuiltInCanonicalPlants();
  const generatedQuery = options.generatedQuery ?? '';
  const shouldLoadGenerated =
    Boolean(options.includeDraftGenerated) &&
    (generatedQuery.trim().length === 0 || generatedQuery.trim().length >= 2);
  const [communityPlants, generatedPlants] = await Promise.all([
    getCommunityPlants(),
    shouldLoadGenerated
      ? searchGeneratedPlants(generatedQuery)
      : Promise.resolve([]),
  ]);

  return mergeCanonicalPlants(
    builtInPlants,
    communityPlants.map(normalizeCommunityCatalogPlant),
    generatedPlants.map(normalizeGeneratedCatalogPlant),
  );
}

export async function searchCanonicalPlantCatalog(
  query: string,
  options: PlantSearchOptions = {},
): Promise<CanonicalPlant[]> {
  const catalog = await listCanonicalPlantCatalog({
    includeDraftGenerated: options.includeDraftGenerated,
    generatedQuery: query,
  });
  const matches = catalog
    .filter(plant => plantMatchesCatalogSearch(plant, query))
    .sort((left, right) => {
      const scoreDelta = getPlantCatalogSearchRelevance(right, query).score - getPlantCatalogSearchRelevance(left, query).score;
      if (scoreDelta !== 0) return scoreDelta;
      return left.name.localeCompare(right.name);
    });

  return typeof options.limit === 'number' ? matches.slice(0, options.limit) : matches;
}

export async function resolveCanonicalPlantsById(
  ids: Iterable<string>,
  options: PlantCatalogOptions = {},
): Promise<CanonicalPlant[]> {
  const orderedIds = Array.from(new Set(Array.from(ids).filter(Boolean)));
  if (orderedIds.length === 0) return [];

  const communityPlants = await getCommunityPlants();

  const basePlants = mergeCanonicalPlants(
    listBuiltInCanonicalPlants(),
    communityPlants.map(normalizeCommunityCatalogPlant),
  );
  let lookup = buildCanonicalPlantLookup(basePlants);

  if (options.includeDraftGenerated) {
    const missingGeneratedIds = orderedIds.filter(id => !lookup.has(id) && id.startsWith('generated-'));
    const generatedPlants = await Promise.all(
      missingGeneratedIds.map(id => getGeneratedPlantById(id)),
    );

    const normalizedGeneratedPlants = generatedPlants
      .filter((plant): plant is NonNullable<typeof plant> => Boolean(plant))
      .map(normalizeGeneratedCatalogPlant);

    lookup = buildCanonicalPlantLookup(mergeCanonicalPlants(basePlants, normalizedGeneratedPlants));
  }

  return orderedIds
    .map(id => lookup.get(id))
    .filter((plant): plant is CanonicalPlant => Boolean(plant));
}

export async function resolveCanonicalPlantLinksByName(
  names: Iterable<string>,
  options: PlantCatalogOptions = {},
): Promise<Map<string, CanonicalPlant>> {
  const requestedNames = Array.from(new Set(
    Array.from(names)
      .map(name => name.trim())
      .filter(Boolean),
  ));
  if (requestedNames.length === 0) return new Map();

  const catalog = await listCanonicalPlantCatalog({
    includeDraftGenerated: options.includeDraftGenerated,
  });
  const links = new Map<string, CanonicalPlant>();

  for (const name of requestedNames) {
    const match = catalog.find(plant => plantNameMatches(plant, name));
    if (match) links.set(name.toLowerCase(), match);
  }

  return links;
}
